import React, { useState } from "react";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import { blogApi } from "../api/blogApi";
import { BlogComment } from "../types/Blog";
import { MessageCircle, Trash2, Send } from "lucide-react";

interface BlogCommentsProps {
  postId: string;
  comments: BlogComment[];
  setComments: React.Dispatch<React.SetStateAction<BlogComment[]>>;
}

const BlogComments: React.FC<BlogCommentsProps> = ({
  postId,
  comments,
  setComments,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [newComment, setNewComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const comment = await blogApi.addComment(postId, newComment.trim());
      setComments((prev) => [...prev, comment]);
      setNewComment("");
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      await blogApi.deleteComment(postId, commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  return (
    <div>
      <h3 className="flex items-center gap-2 text-lg font-semibold text-white mb-6">
        <MessageCircle className="w-5 h-5 text-gray-500" />
        Comments
        {comments.length > 0 && (
          <span className="text-sm font-normal text-gray-600">
            ({comments.length})
          </span>
        )}
      </h3>

      {/* Comment form */}
      {user ? (
        <form onSubmit={handleSubmit} className="mb-8">
          <div className="flex gap-3">
            {user.photoURL ? (
              <img
                src={user.photoURL}
                alt={user.displayName || "User"}
                className="w-9 h-9 rounded-full flex-shrink-0"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 text-sm font-medium flex-shrink-0">
                {user.displayName?.charAt(0)?.toUpperCase() || "U"}
              </div>
            )}
            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a comment..."
                rows={3}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-xl text-gray-300 placeholder-gray-600 text-sm focus:outline-none focus:ring-1 focus:ring-gray-700 resize-none transition-colors"
              />
              <div className="flex justify-end mt-2">
                <button
                  type="submit"
                  disabled={!newComment.trim() || isSubmitting}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-white text-gray-950 rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-3.5 h-3.5" />
                  Post
                </button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="mb-8 p-4 bg-gray-900/50 border border-gray-800 rounded-xl text-center">
          <p className="text-gray-500 text-sm">
            <button
              onClick={() => navigate("/")}
              className="text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              Sign in
            </button>{" "}
            to leave a comment.
          </p>
        </div>
      )}

      {/* Comments list */}
      <div className="space-y-6">
        {comments.length === 0 ? (
          <p className="text-gray-600 text-sm py-4">
            No comments yet. Be the first to share your thoughts.
          </p>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="flex gap-3">
              {comment.authorPhoto ? (
                <img
                  src={comment.authorPhoto}
                  alt={comment.author}
                  className="w-9 h-9 rounded-full flex-shrink-0"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 text-sm font-medium flex-shrink-0">
                  {comment.author?.charAt(0)?.toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-300">
                    {comment.author}
                  </span>
                  <span className="text-xs text-gray-700">
                    {formatDate(comment.createdAt)}
                  </span>
                  {user && user.displayName === comment.author && (
                    <button
                      onClick={() => handleDelete(comment.id)}
                      className="ml-auto p-1 text-gray-700 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap">
                  {comment.content}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BlogComments;
